import {Controller, useForm, useFormContext, useWatch} from 'react-hook-form'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Modal, ModalBody, ModalFooter, ModalOverlay, ModalTitle} from '@/components/ui/modal-atoms/ModalAtoms'
import {Button} from '@/components/ui/button/Button'
import {useTranslation} from '@/translations/i18n'
import {StyledUserResidenceFormModalHeader} from './style'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {InputSelectCity} from '@/components/commons/input-select-city/InputSelectCity'
import {InputSelectProvince} from '@/components/commons/input-select-province/InputSelectProvince'
import {InputSelectAsl} from '@/components/commons/input-select-asl/InputSelectAsl'
import {zodResolver} from '@hookform/resolvers/zod'
import {UserResidenceFormValidationSchema, UserResidenceManualFormModalValidationSchema} from '@/features/users/types'

export const UserResidenceManualFormModal = ({
    onClose,
    defaultValues
}: {
    onClose: () => void
    defaultValues: UserResidenceFormValidationSchema
}) => {
    const {t} = useTranslation()

    // methods
    const parentForm = useFormContext<UserResidenceFormValidationSchema>()
    const form = useForm<UserResidenceManualFormModalValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserResidenceManualFormModalValidationSchema),
        defaultValues
    })

    // form observer
    const city = useWatch({control: form.control, name: 'residence.city'})

    // actions
    const handleSubmit = form.handleSubmit(formValues => {
        parentForm.setValue('residence.address', formValues.residence.address, {shouldDirty: true})
        parentForm.setValue('residence.streetNumber', formValues.residence.streetNumber, {shouldDirty: true})
        parentForm.setValue('residence.city', formValues.residence.city, {shouldDirty: true})
        parentForm.setValue('residence.province', formValues.residence.province, {shouldDirty: true})
        parentForm.setValue('residence.postalCode', formValues.residence.postalCode, {shouldDirty: true})
        parentForm.setValue('residenceAslDistrict', formValues.residenceAslDistrict ?? null, {shouldDirty: true})
        parentForm.clearErrors('residence')
        onClose()
    })

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="600px" overflowY="visible">
                <StyledUserResidenceFormModalHeader>
                    <Flexbox justify="space-between" align="center">
                        <ModalTitle>{t('commons:residence')}</ModalTitle>
                        <Button shape="text" variant="ghost" onClick={onClose}>
                            <XCloseIcon />
                        </Button>
                    </Flexbox>
                </StyledUserResidenceFormModalHeader>
                <ModalBody>
                    <form>
                        <Grid columns={6} columnGap={2.5} rowGap={4}>
                            <GridItem startColumn={1} endColumn={4}>
                                <InputText
                                    type="text"
                                    label={t('commons:address')}
                                    placeholder={t('commons:address_placeholder')}
                                    errorMessage={form.formState.errors.residence?.address?.message}
                                    {...form.register('residence.address')}
                                />
                            </GridItem>
                            <GridItem startColumn={4} endColumn={5}>
                                <InputText
                                    type="text"
                                    label={t('commons:street_number')}
                                    placeholder="10"
                                    errorMessage={form.formState.errors.residence?.streetNumber?.message}
                                    {...form.register('residence.streetNumber')}
                                />
                            </GridItem>
                            <GridItem startColumn={1} endColumn={4}>
                                <Controller
                                    control={form.control}
                                    name="residence.city"
                                    render={({field}) => (
                                        <InputSelectCity
                                            label={t('commons:town')}
                                            value={field.value}
                                            placeholder={t('commons:select')}
                                            errorMessage={form.formState.errors.residence?.city?.message}
                                            onChange={value => {
                                                field.onChange(value)
                                                form.trigger('residence.city')
                                                form.setValue('residenceAslDistrict', null)
                                                if (value?.province) {
                                                    form.setValue('residence.province', value.province)
                                                    form.trigger('residence.province')
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </GridItem>
                            <GridItem startColumn={4} endColumn={6}>
                                <Controller
                                    control={form.control}
                                    name="residence.province"
                                    render={({field}) => (
                                        <InputSelectProvince
                                            label={t('commons:province')}
                                            value={field.value}
                                            errorMessage={form.formState.errors.residence?.province?.message}
                                            placeholder={t('commons:select')}
                                            onChange={value => {
                                                field.onChange(value)
                                                form.setValue('residence.city', null)
                                                form.setValue('residenceAslDistrict', null)
                                            }}
                                        />
                                    )}
                                />
                            </GridItem>
                            <GridItem startColumn={6} endColumn={7}>
                                <InputText
                                    type="text"
                                    label={t('commons:zip_code')}
                                    placeholder="10"
                                    errorMessage={form.formState.errors.residence?.postalCode?.message}
                                    {...form.register('residence.postalCode')}
                                />
                            </GridItem>
                            <GridItem startColumn={1} endColumn={4}>
                                <Controller
                                    control={form.control}
                                    name="residenceAslDistrict"
                                    render={({field}) => (
                                        <InputSelectAsl
                                            label={t('commons:residence_asl')}
                                            value={field.value}
                                            isPortal={false}
                                            city={city ?? null}
                                            errorMessage={form.formState.errors.residenceAslDistrict?.message}
                                            placeholder={t('commons:select')}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </GridItem>
                        </Grid>
                    </form>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant="secondary">
                        {t('commons:cancel')}
                    </Button>
                    <Button type="submit" onClick={handleSubmit} variant="primaryNeutral">
                        {t('commons:confirm')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
